import { takeLatest, put, call, race, take, delay } from 'redux-saga/effects';
import {
  TRIGGER_CRON,
  FETCH_CRON_STATUS,
  FETCH_CRON_RUNS,
  START_POLLING,
  STOP_POLLING,
  RESET_CRON,
  CLEAR_AGGREGATION_CACHE,
  setTriggerLoadingAction,
  setCurrentModalAction,
  setCronStatusAction,
  setCronRunsAction,
  startPollingAction,
  setResetLoadingAction,
  setClearCacheLoadingAction,
} from '../actions';
import {
  triggerCaseReportCron,
  fetchCronStatus,
  fetchCronRuns,
  resetCronState,
  clearAggregationCache,
} from '../../../api';
import { POPUP_ALERT } from '../../../actions/alert';

const { error: errLog } = console;

const POLL_INTERVAL_MS = 10000;

function* pollLoop() {
  while (true) {
    yield delay(POLL_INTERVAL_MS);
    try {
      const { data } = yield call(fetchCronStatus);
      yield put(setCronStatusAction(data));
      if (!data.isCronRunning) return;
    } catch (e) {
      errLog('[EngagementCaseReport] Poll failed:', e);
      return;
    }
  }
}

function* pollCronStatusSaga() {
  yield race({
    poll: call(pollLoop),
    stop: take(STOP_POLLING),
  });
}

function* triggerCronSaga({ data: { params } }) {
  const queueOnly = params.queueOnly === true;
  try {
    yield put(setTriggerLoadingAction(true));
    const { data } = yield call(triggerCaseReportCron, params);
    yield put(setTriggerLoadingAction(false));
    yield put(setCurrentModalAction(null));

    if (queueOnly) {
      try {
        const { data: status } = yield call(fetchCronStatus);
        yield put(setCronStatusAction(status));
      } catch (pollErr) {
        errLog(
          '[EngagementCaseReport] Status refresh after queue failed:',
          pollErr
        );
      }
      yield put(startPollingAction());
    } else {
      yield put(
        setCronStatusAction({
          isCronRunning: true,
          startedAt: new Date().toISOString(),
          months: params.months || [],
          currentMonth: null,
          completedMonths: [],
          force: params.force || false,
          hubIds: params.hubIds || [],
        })
      );
      yield put(startPollingAction());
    }

    yield put({
      type: POPUP_ALERT,
      data: {
        type: 'success',
        message:
          data.message ||
          (queueOnly
            ? 'Job queued successfully. The worker will pick it up when ready.'
            : 'Engagement case report cron triggered successfully'),
      },
    });
  } catch (e) {
    yield put(setTriggerLoadingAction(false));
    errLog(e);
    let errorMessage;
    if (e.response && e.response.data && e.response.data.error) {
      errorMessage =
        e.response.data.error.user_message || e.response.data.error;
    } else if (queueOnly) {
      errorMessage = 'Error queueing engagement case report job';
    } else {
      errorMessage = 'Error triggering engagement case report cron';
    }
    yield put({
      type: POPUP_ALERT,
      data: { type: 'error', message: errorMessage },
    });
  }
}

function* fetchCronStatusSaga() {
  try {
    const { data } = yield call(fetchCronStatus);
    yield put(setCronStatusAction(data));
    if (data.isCronRunning) {
      yield put(startPollingAction());
    }
  } catch (e) {
    errLog(e);
  }
}

function* fetchCronRunsSaga({ data: payload = {} }) {
  try {
    yield put(setCronRunsAction({ cronRunsLoading: true }));
    const { data: response } = yield call(fetchCronRuns, payload);
    const result = response.data || response;
    yield put(
      setCronRunsAction({
        cronRunsLoading: false,
        cronRuns: result.runs || [],
        cronRunsTotal: result.total || 0,
        cronRunsPage: result.page || 1,
        cronRunsPageSize: result.pageSize || 10,
      })
    );
  } catch (e) {
    yield put(setCronRunsAction({ cronRunsLoading: false }));
    errLog(e);
  }
}

function* resetCronSaga() {
  try {
    yield put(setResetLoadingAction(true));
    yield call(resetCronState);
    const { data } = yield call(fetchCronStatus);
    yield put(setCronStatusAction(data));
    yield put(setResetLoadingAction(false));
    yield put({
      type: POPUP_ALERT,
      data: {
        type: 'success',
        message: 'Cron state reset. You can now trigger a new run.',
      },
    });
  } catch (e) {
    yield put(setResetLoadingAction(false));
    errLog('[EngagementCaseReport] Reset failed:', e);
    const errorMessage =
      e.response && e.response.data && e.response.data.error
        ? e.response.data.error.user_message || e.response.data.error
        : 'Failed to reset cron state';
    yield put({
      type: POPUP_ALERT,
      data: { type: 'error', message: errorMessage },
    });
  }
}

function* clearAggregationCacheSaga({ data: { params } }) {
  try {
    yield put(setClearCacheLoadingAction(true));
    const { data } = yield call(clearAggregationCache, params);
    yield put(setClearCacheLoadingAction(false));
    yield put(setCurrentModalAction(null));
    yield put({
      type: POPUP_ALERT,
      data: {
        type: 'success',
        message:
          data.message ||
          `Cleared cached aggregation jobs for hub ${params.hubId}.`,
      },
    });
  } catch (e) {
    yield put(setClearCacheLoadingAction(false));
    errLog('[EngagementCaseReport] Clear aggregation cache failed:', e);
    const errorMessage =
      e.response && e.response.data && e.response.data.error
        ? e.response.data.error.user_message || e.response.data.error
        : 'Failed to clear aggregation cache';
    yield put({
      type: POPUP_ALERT,
      data: { type: 'error', message: errorMessage },
    });
  }
}

const EngagementCaseReportSaga = [
  takeLatest(TRIGGER_CRON, triggerCronSaga),
  takeLatest(FETCH_CRON_STATUS, fetchCronStatusSaga),
  takeLatest(FETCH_CRON_RUNS, fetchCronRunsSaga),
  takeLatest(RESET_CRON, resetCronSaga),
  takeLatest(CLEAR_AGGREGATION_CACHE, clearAggregationCacheSaga),
  takeLatest(START_POLLING, pollCronStatusSaga),
];

export default EngagementCaseReportSaga;
